import React, { useState, useMemo } from 'react';
import { Calculator as CalcIcon } from 'lucide-react';
import { Input } from './ui/input';
import { Label } from './ui/label';

export default function Calculator() {
  const [orders, setOrders] = useState(1000);
  const [items, setItems] = useState(1.5);
  const [shipping, setShipping] = useState(8);
  const [pallets, setPallets] = useState(5);

  const calc = useMemo(() => {
    const traditionalPick = orders * (3 + (items - 1) * 0.25);
    const wasabiPick = orders * 2;
    const traditionalStorage = pallets * 25;
    const wasabiStorage = pallets * 25 * 0.6;
    const traditionalShipping = orders * shipping;
    const wasabiShipping = orders * shipping * 0.75;

    const traditionalTotal = traditionalPick + traditionalStorage + traditionalShipping;
    const wasabiTotal = wasabiPick + wasabiStorage + wasabiShipping;
    const monthlySave = Math.max(traditionalTotal - wasabiTotal, 0);
    const yearlySave = monthlySave * 12;
    const annualTraditional = traditionalTotal * 12;
    const annualWasabi = wasabiTotal * 12;
    const savingsPct = traditionalTotal > 0 ? (monthlySave / traditionalTotal) * 100 : 0;

    return {
      traditionalPick,
      wasabiPick,
      traditionalStorage,
      wasabiStorage,
      traditionalShipping,
      wasabiShipping,
      traditionalTotal,
      wasabiTotal,
      monthlySave,
      yearlySave,
      annualTraditional,
      annualWasabi,
      savingsPct,
    };
  }, [orders, items, shipping, pallets]);

  const fmt = (n) =>
    n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });

  return (
    <section id="calculator" className="py-20 sm:py-28 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-indigo-50 border border-indigo-100 text-indigo-700 text-sm font-semibold mb-4">
            <CalcIcon className="w-4 h-4" />
            Cost Comparison
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900">
            See How Much You&apos;ll Save
          </h2>
          <p className="mt-4 text-lg text-slate-600">
            Plug in your numbers. See the real cost difference.
          </p>
        </div>

        {/* Inputs */}
        <div className="bg-white border border-slate-200 rounded-2xl p-7 mb-6">
          <h3 className="text-xl font-bold text-slate-900 mb-6">Your Business Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label htmlFor="orders" className="text-sm font-medium text-slate-600 mb-2 block">
                Monthly Orders
              </Label>
              <Input
                id="orders"
                type="number"
                min="0"
                value={orders}
                onChange={(e) => setOrders(Number(e.target.value) || 0)}
                className="h-12 text-base rounded-lg"
              />
            </div>
            <div>
              <Label htmlFor="items" className="text-sm font-medium text-slate-600 mb-2 block">
                Avg Items per Order
              </Label>
              <Input
                id="items"
                type="number"
                step="0.1"
                min="1"
                value={items}
                onChange={(e) => setItems(Number(e.target.value) || 1)}
                className="h-12 text-base rounded-lg"
              />
            </div>
            <div>
              <Label htmlFor="shipping" className="text-sm font-medium text-slate-600 mb-2 block">
                Avg Shipping Cost ($)
              </Label>
              <Input
                id="shipping"
                type="number"
                step="0.5"
                min="0"
                value={shipping}
                onChange={(e) => setShipping(Number(e.target.value) || 0)}
                className="h-12 text-base rounded-lg"
              />
            </div>
            <div>
              <Label htmlFor="pallets" className="text-sm font-medium text-slate-600 mb-2 block">
                Number of Pallets
              </Label>
              <Input
                id="pallets"
                type="number"
                min="0"
                value={pallets}
                onChange={(e) => setPallets(Number(e.target.value) || 0)}
                className="h-12 text-base rounded-lg"
              />
            </div>
          </div>
        </div>

        {/* Comparison */}
        <div className="bg-indigo-50/40 border border-indigo-100 rounded-2xl p-6 sm:p-8">
          <h3 className="text-xl font-bold text-slate-900">Monthly Cost Comparison</h3>
          <p className="text-slate-600 mb-6">Here&apos;s how the math breaks down</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* Traditional */}
            <div className="bg-white rounded-xl p-5 border border-slate-200">
              <div className="text-center mb-4">
                <p className="text-base font-bold text-slate-900">Traditional 3PL</p>
                <p className="text-xs text-slate-500">Full Pallet Charges</p>
              </div>
              <div className="space-y-3">
                <Row label="Pick & Pack" sub={`${orders} orders \u00d7 ($3 + ${(items - 1).toFixed(1)} \u00d7 $0.25)`} value={fmt(calc.traditionalPick)} />
                <Row label="Storage (Full Pallet)" sub={`${pallets} pallets \u00d7 $25/pallet`} value={fmt(calc.traditionalStorage)} muted="Charged for full pallets reserved." />
                <Row label="Shipping Costs" value={fmt(calc.traditionalShipping)} />
              </div>
              <div className="mt-5 pt-4 border-t border-slate-200">
                <p className="text-sm text-slate-500">Total Monthly Cost</p>
                <p className="text-2xl font-extrabold text-slate-900">{fmt(calc.traditionalTotal)}</p>
                <p className="text-sm text-slate-500 mt-2">Annual Cost</p>
                <p className="text-lg font-bold text-slate-700">{fmt(calc.annualTraditional)}</p>
              </div>
            </div>

            {/* Shipmaster */}
            <div className="rounded-xl p-5 border-2 border-indigo-300 bg-gradient-to-br from-indigo-50 to-white">
              <div className="text-center mb-4">
                <p className="text-base font-bold text-indigo-600">Shipmaster</p>
                <p className="text-xs text-slate-500">Pallet-Based, Prorated Storage</p>
              </div>
              <div className="space-y-3">
                <Row label="Pick & Pack" sub={`${orders} orders \u00d7 $2`} value={fmt(calc.wasabiPick)} highlight />
                <Row label="Storage ($25/pallet)" sub={`${pallets} pallets \u00d7 $25 \u00d7 ~60% utilization`} value={fmt(calc.wasabiStorage)} highlight muted="You only pay for the space your products occupy." />
                <Row label="Shipping Costs" value={fmt(calc.wasabiShipping)} highlight />
              </div>
              <div className="mt-5 pt-4 border-t border-indigo-200">
                <p className="text-sm text-indigo-600 font-medium">Shipmaster Monthly Cost</p>
                <p className="text-2xl font-extrabold text-indigo-600">{fmt(calc.wasabiTotal)}</p>
                <p className="text-sm text-indigo-600 font-medium mt-2">Shipmaster Annual Cost</p>
                <p className="text-lg font-bold text-indigo-700">{fmt(calc.annualWasabi)}</p>
              </div>
            </div>
          </div>

          {/* Savings banner */}
          <div className="mt-6 brand-gradient rounded-2xl p-7 text-white text-center">
            <p className="text-sm uppercase tracking-widest opacity-90 mb-2">You Save</p>
            <p className="text-5xl sm:text-6xl font-extrabold tracking-tight">
              {calc.savingsPct.toFixed(1)}%
            </p>
            <p className="mt-3 text-2xl font-bold">{fmt(calc.monthlySave)}/month</p>
            <p className="text-base opacity-90">{fmt(calc.yearlySave)}/year</p>
          </div>

          <div className="mt-5 flex items-start gap-3 p-4 bg-white border border-indigo-100 rounded-xl">
            <span className="flex-shrink-0 w-8 h-8 rounded-full bg-indigo-50 flex items-center justify-center">
              <CalcIcon className="w-4 h-4 text-indigo-600" />
            </span>
            <p className="text-sm text-slate-600">
              <span className="font-bold text-slate-900">Shipmaster Storage Pricing:</span>{' '}
              Storage is $25/pallet, prorated to the space your products actually occupy. Traditional 3PLs charge for full pallets regardless of how much space you actually use.
            </p>
          </div>
          <p className="mt-3 text-xs text-slate-500">
            * Traditional 3PL costs are estimates based on industry averages. Actual costs may vary by provider.
          </p>
        </div>
      </div>
    </section>
  );
}

function Row({ label, sub, value, highlight, muted }) {
  return (
    <div className={`flex items-start justify-between gap-3 p-3 rounded-lg ${highlight ? 'bg-indigo-50/60' : 'bg-slate-50'}`}>
      <div className="flex-1">
        <p className="text-sm font-semibold text-slate-900">{label}</p>
        {sub && <p className="text-xs text-slate-500 mt-0.5">{sub}</p>}
        {muted && <p className="text-xs text-slate-400 mt-1 italic">{muted}</p>}
      </div>
      <span className={`text-base font-bold whitespace-nowrap ${highlight ? 'text-indigo-600' : 'text-slate-900'}`}>
        {value}
      </span>
    </div>
  );
}
